import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Database,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Calendar,
  Building2,
  Play,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Activity,
  Zap
} from 'lucide-react';
import SyncSchedulerStatus from './SyncSchedulerStatus';
import DataReconciliation from './DataReconciliation';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function DataSyncHistory() {
  const { isSuperAdmin } = useAuth();
  
  const [activeTab, setActiveTab] = useState('sync');  // 'sync' | 'reconcile'
  const [syncStatuses, setSyncStatuses] = useState([]);
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch comprehensive sync progress
  const fetchSyncProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/settings/sync-progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSyncProgress(data);
      }
    } catch (error) {
      console.error('Error fetching sync progress:', error);
    }
  }, []);

  // Fetch all brands
  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/brands`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, []);

  // Fetch sync status for all brands
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/settings/sync-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStatuses(data.statuses || []);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, []);

  // Fetch available dates
  const fetchAvailableDates = useCallback(async () => {
    try {
      const url = selectedBrand !== 'all' 
        ? `${API}/api/settings/sync-history/dates?brand_id=${selectedBrand}`
        : `${API}/api/settings/sync-history/dates`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.dates || []);
      }
    } catch (error) {
      console.error('Error fetching dates:', error);
    }
  }, [selectedBrand]);

  // Fetch sync history
  const fetchSyncHistory = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/settings/sync-history?date=${selectedDate}`;
      if (selectedBrand !== 'all') {
        url += `&brand_id=${selectedBrand}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching sync history:', error);
      toast.error('Failed to load sync history');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedBrand]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBrands();
      fetchSyncStatus();
      fetchSyncProgress();
    }
  }, [isSuperAdmin, fetchBrands, fetchSyncStatus, fetchSyncProgress]);

  // Auto-refresh sync progress every 10 seconds when enabled
  useEffect(() => {
    if (!isSuperAdmin || !autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchSyncProgress();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [isSuperAdmin, autoRefresh, fetchSyncProgress]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAvailableDates();
      fetchSyncHistory();
    }
  }, [isSuperAdmin, fetchAvailableDates, fetchSyncHistory]);

  // Trigger manual sync
  const triggerSync = async (brandId = null) => {
    setTriggering(true);
    try {
      const url = brandId 
        ? `${API}/api/settings/sync/trigger?brand_id=${brandId}`
        : `${API}/api/settings/sync/trigger`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Sync triggered successfully');
        fetchSyncStatus();
        fetchSyncHistory();
      } else {
        toast.error('Failed to trigger sync');
      }
    } catch (error) {
      console.error('Error triggering sync:', error);
      toast.error('Error triggering sync');
    } finally {
      setTriggering(false);
    }
  };

  // Navigate date
  const navigateDate = (direction) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + direction);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    // Force IST timezone display
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    // Force IST timezone display
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  const formatFullDateTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    // Full date time in IST
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const config = {
      completed: { icon: CheckCircle2, color: 'bg-green-500/20 text-green-400', label: 'Completed' },
      started: { icon: Activity, color: 'bg-blue-500/20 text-blue-400', label: 'In Progress' },
      failed: { icon: XCircle, color: 'bg-red-500/20 text-red-400', label: 'Failed' },
      triggered: { icon: Zap, color: 'bg-yellow-500/20 text-yellow-400', label: 'Triggered' },
      syncing: { icon: RefreshCw, color: 'bg-blue-500/20 text-blue-400', label: 'Syncing' }
    };
    const c = config[status] || config.started;
    const Icon = c.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${c.color}`}>
        <Icon className={`w-3 h-3 ${status === 'syncing' ? 'animate-spin' : ''}`} />
        {c.label}
      </span>
    );
  };

  // Event type badge
  const EventTypeBadge = ({ type }) => {
    const config = {
      full_sync: { color: 'bg-purple-500/20 text-purple-400', label: 'Full Sync' },
      incremental: { color: 'bg-cyan-500/20 text-cyan-400', label: 'Incremental' },
      stats_only: { color: 'bg-slate-500/20 text-slate-400', label: 'Stats Only' },
      manual_trigger: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Manual' }
    };
    const c = config[type] || config.stats_only;
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Database className="w-12 h-12 mb-4 opacity-50" />
        <p>Only super admins can access Data Sync History</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-sync-history">
      {/* Header with tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-primary" />
            Data Sync Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatic sync runs every 1 hour with email notifications - All times shown in IST
          </p>
        </div>
        {/* Tab selector */}
        <div className="inline-flex rounded-lg bg-muted/50 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('sync')}
            className={`px-4 py-2 rounded text-sm font-medium transition ${
              activeTab === 'sync'
                ? 'bg-card shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-sync"
          >
            Sync Progress
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reconcile')}
            className={`px-4 py-2 rounded text-sm font-medium transition ${
              activeTab === 'reconcile'
                ? 'bg-card shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-reconcile"
          >
            Reconciliation
          </button>
        </div>
      </div>

      {activeTab === 'reconcile' ? (
        <DataReconciliation />
      ) : (
        <SyncProgressView
          loading={loading}
          triggering={triggering}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          brands={brands}
          syncStatuses={syncStatuses}
          syncProgress={syncProgress}
          syncHistory={syncHistory}
          availableDates={availableDates}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          triggerSync={triggerSync}
          navigateDate={navigateDate}
          formatTime={formatTime}
          formatDateTime={formatDateTime}
          StatusBadge={StatusBadge}
          EventTypeBadge={EventTypeBadge}
          fetchSyncStatus={fetchSyncStatus}
          fetchSyncProgress={fetchSyncProgress}
          fetchSyncHistory={fetchSyncHistory}
        />
      )}
    </div>
  );
}

function SyncProgressView({
  loading, triggering, selectedBrand, setSelectedBrand, selectedDate, setSelectedDate,
  brands, syncStatuses, syncProgress, syncHistory, availableDates,
  autoRefresh, setAutoRefresh, triggerSync, navigateDate,
  formatTime, formatDateTime, StatusBadge, EventTypeBadge,
  fetchSyncStatus, fetchSyncProgress, fetchSyncHistory,
}) {
  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center gap-3 justify-end">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input 
            type="checkbox" 
            checked={autoRefresh} 
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded"
          />
          Auto-refresh
        </label>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => { fetchSyncStatus(); fetchSyncProgress(); fetchSyncHistory(); }}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button 
          onClick={() => triggerSync(selectedBrand !== 'all' ? selectedBrand : null)}
          disabled={triggering}
          data-testid="trigger-sync-btn"
        >
          {triggering ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          Trigger Sync
        </Button>
      </div>

      {/* Sync Scheduler Status - NEW */}
      <SyncSchedulerStatus />

      {/* Global Sync Status */}
      {syncProgress?.global_lock?.locked && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            <div>
              <p className="font-medium text-blue-400">Sync In Progress</p>
              <p className="text-sm text-muted-foreground">
                Running for {syncProgress.global_lock.duration_minutes?.toFixed(1)} minutes
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {syncProgress && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-primary">{syncProgress.total_brands}</p>
            <p className="text-sm text-muted-foreground">Total Brands</p>
          </div>
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{syncProgress.brands_fully_synced}</p>
            <p className="text-sm text-muted-foreground">Fully Synced</p>
          </div>
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{syncProgress.brands_syncing}</p>
            <p className="text-sm text-muted-foreground">Syncing</p>
          </div>
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{syncProgress.brands_pending}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </div>
        </div>
      )}

      {/* Comprehensive Sync Progress Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Sync Progress by Brand
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Brand</th>
                <th className="text-left p-3 font-medium">Source</th>
                <th className="text-left p-3 font-medium">Progress</th>
                <th className="text-right p-3 font-medium">Customers</th>
                <th className="text-right p-3 font-medium">Transactions</th>
                <th className="text-right p-3 font-medium">Stores</th>
                <th className="text-left p-3 font-medium">Last Sync</th>
                <th className="text-left p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {syncProgress?.brands?.map((brand) => (
                <tr key={brand.brand_id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-3">
                    <div className="font-medium">{brand.name}</div>
                    <div className="text-xs text-muted-foreground">{brand.brand_id}</div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      brand.source_type === 'manual_csv' 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-cyan-500/20 text-cyan-400'
                    }`}>
                      {brand.source_type === 'manual_csv' ? 'CSV' : 'Data Bridge'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            brand.sync_percentage >= 95 ? 'bg-green-500' :
                            brand.sync_percentage >= 50 ? 'bg-blue-500' :
                            brand.sync_percentage > 0 ? 'bg-yellow-500' : 'bg-muted-foreground'
                          }`}
                          style={{ width: `${brand.sync_percentage}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium w-12">{brand.sync_percentage}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">
                    <div>{brand.local_customers?.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">/ {brand.total_customers?.toLocaleString()}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{brand.local_transactions?.toLocaleString()}</td>
                  <td className="p-3 text-right font-mono">{brand.local_stores?.toLocaleString()}</td>
                  <td className="p-3">
                    {brand.last_sync_time ? (
                      <div>
                        <div className="text-xs">{formatDateTime(brand.last_sync_time)}</div>
                        <div className="text-xs text-muted-foreground">
                          {brand.minutes_since_sync < 60 
                            ? `${brand.minutes_since_sync?.toFixed(0)}m ago`
                            : `${(brand.minutes_since_sync / 60)?.toFixed(1)}h ago`
                          }
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Never</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      {brand.health === 'healthy' && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                      {brand.health === 'syncing' && <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />}
                      {brand.health === 'warning' && <AlertCircle className="w-4 h-4 text-yellow-400" />}
                      {brand.health === 'pending' && <Clock className="w-4 h-4 text-muted-foreground" />}
                      <span className={`text-xs ${
                        brand.health === 'healthy' ? 'text-green-400' :
                        brand.health === 'syncing' ? 'text-blue-400' :
                        brand.health === 'warning' ? 'text-yellow-400' : 'text-muted-foreground'
                      }`}>
                        {brand.health_reason}
                      </span>
                    </div>
                    {brand.current_progress?.current_step && brand.current_progress.status === 'syncing' && (
                      <div className="text-xs text-blue-400 mt-1">
                        {brand.current_progress.current_step.replace(/_/g, ' ')}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Brand Filter */}
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedBrand} onValueChange={setSelectedBrand}>
            <SelectTrigger className="w-48" data-testid="brand-filter">
              <SelectValue placeholder="All Brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              {brands.map((brand) => (
                <SelectItem key={brand.brand_id} value={brand.brand_id?.toString()}>
                  {brand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
            data-testid="date-picker"
          />
          <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
          >
            Today
          </Button>
        </div>
      </div>

      {/* Sync History Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Sync Events for {new Date(selectedDate).toLocaleDateString('en-IN', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric',
              timeZone: 'Asia/Kolkata'
            })}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : syncHistory.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No sync events for this date
                  </td>
                </tr>
              ) : (
                syncHistory.map((event, idx) => {
                  const brandName = event.brand_name || brands.find(b => String(b.brand_id) === String(event.brand_id))?.name || `Brand ${event.brand_id}`;
                  return (
                    <tr key={idx} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono">
                        {formatTime(event.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {brandName}
                      </td>
                      <td className="px-4 py-3">
                        <EventTypeBadge type={event.event_type} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={event.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-md">
                        <div className="space-y-1">
                          {event.data_bridge_url && event.data_bridge_url !== 'N/A' && (
                            <div className="text-xs truncate" title={event.data_bridge_url}>
                              <span className="text-muted-foreground">Server:</span> {event.data_bridge_url}
                            </div>
                          )}
                          {event.details?.local_records && (
                            <div className="text-xs">
                              <span className="text-green-400">{event.details.local_records.customers?.toLocaleString() || 0}</span> customers, {' '}
                              <span className="text-blue-400">{event.details.local_records.transactions?.toLocaleString() || 0}</span> txns
                            </div>
                          )}
                          {event.details?.error && (
                            <div className="text-red-400 text-xs truncate" title={event.details.error}>
                              Error: {event.details.error}
                            </div>
                          )}
                          {event.details?.duration_seconds && (
                            <div className="text-xs text-muted-foreground">
                              Duration: {event.details.duration_seconds.toFixed(1)}s
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Available Dates (Quick Access) */}
      {availableDates.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            Recent Sync Dates
          </h3>
          <div className="flex flex-wrap gap-2">
            {availableDates.slice(0, 14).map((d) => (
              <Button
                key={d.date}
                variant={selectedDate === d.date ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDate(d.date)}
                className="text-xs"
              >
                {new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                <Badge variant="secondary" className="ml-1 text-[10px]">
                  {d.sync_count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
